import { Component, OnInit, OnDestroy, inject, effect, signal, computed  } from '@angular/core';
import { SwUpdate, VersionReadyEvent, SwPush } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { AnalyticsService } from './services/analytics.service';
import { NotificacionService } from './services/notificacion.service';
import { NavigationEnd, Router, ActivatedRoute, ActivationEnd } from '@angular/router';
import { Meta } from '@angular/platform-browser';
import { SplashScreen } from '@capacitor/splash-screen';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { LoadingService } from './services/loading.service';
import { Subscription } from 'rxjs';
import { IS_NATIVE } from './shared/platform.util';
import { NetworkService } from './services/network.service';
import { UpdateCheckService } from './services/update-check.service';
import { UsuarioService } from './services/usuario.service';

//const DEFAULT_THEME = '#334986';
const LOADING_THEME = '#fff';
const DEFAULT_THEME = '#fff';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private _network = inject(NetworkService);
  private readonly _authService = inject(AuthService);
  private readonly _analytics = inject(AnalyticsService);
  private readonly _notificacionService = inject(NotificacionService);
  private readonly _usuarioService = inject(UsuarioService);
  private meta = inject(Meta);
  private _loadingService = inject(LoadingService);
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly swPush = inject(SwPush, { optional: true });
  private readonly router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly _updateCheck = inject(UpdateCheckService);

  // === estado (signals) ===
  modalVersion = signal<boolean>(false);
  isBlur = signal<string>('');
  isLoading = signal<boolean>(false);
  hideNavbar = signal<boolean>(false);
  // rutas donde el contenido debe llegar pegado al borde del sidebar en desktop
  // (ver .page-wrapper.full-bleed-left en styles.css)
  fullBleedLeft = signal<boolean>(false);
  private fullBleedLeftRoutes = ['/notifications'];
  networkState = this._network.state; // signal de NetworkService
  showUpdateModal = computed(() => this._updateCheck.updateAvailable());

  // si querés ocultar navbar en estas rutas:
  private hideNavbarRoutes = [
    '/sign-in','/sign-up','/verify-email','/recover-password','/politica-privacidad','/terminos-condiciones'
  ];

  private subs = new Subscription();
  routeThemeColor = signal<string>(DEFAULT_THEME);
  /** para asegurarnos de no correr dos veces la inicialización de notifs */
  private notifInitOnce = false;

  constructor() {
    effect(() => {
      this.isLoading.set(this._loadingService.isLoading());
    });
    // color final de la barra: loading ? LOADING_THEME : color de la ruta
    effect(() => {
      const finalColor = this._loadingService.isLoading() ? LOADING_THEME : this.routeThemeColor();
      this.meta.updateTag({ name: 'theme-color', content: finalColor });
    });
    // Cuando authReady() esté listo, decidimos si inicializar notificaciones
    effect(() => {
      const ready = this._authService.authReady();
      if (!ready || this.notifInitOnce) return;

      const session = this._authService.session();          // 'guest' | 'web' | 'native'
      const user = this._authService.currentUser();         // Firebase user o null

      // Email verificado: en web viene de user.emailVerified;
      // en nativo lo resolviste y guardaste en localStorage (emailVerified: '1'|'0')
      const emailVerified =
        session === 'native'
          ? localStorage.getItem('emailVerified') === '1'
          : !!user?.emailVerified;

      // Si no está verificado, no hacemos nada acá.
      // (Tu ensureSession ya navega a /verify-email o limpia sesión según el caso)
      if (!emailVerified) return;

      const tokenBackend = localStorage.getItem('tokenBackend');
      if (!tokenBackend) return;

      const idUsuario = this._usuarioService.usuario().id;
      if (!idUsuario) return;

      this.notifInitOnce = true;

      // En apps nativas: inicializar listeners al arrancar la app para capturar
      // rotaciones automáticas de token APNs/FCM que hace el sistema operativo
      if (IS_NATIVE) {
        this._notificacionService.setupNotificationListeners()
          .catch(err => console.error('Error configurando listeners de notificaciones:', err));
      }

      // Correr la verificación de notificaciones con loader
      this._loadingService
        .run(async () => {
          await this._notificacionService.verificarEstadoNotificaciones(user?.uid!);
        })
        .catch(err => console.error('Error verificando notificaciones:', err));
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      if (IS_NATIVE) {
        await this.setupDeepLinks();
      }

      // ==================================================
      // 🔵 CAMBIAR COLOR DE TEMA + APLICAR CLASE POR RUTA
      // ==================================================
      const themeSub = this.router.events
        .pipe(
          filter((e): e is ActivationEnd => e instanceof ActivationEnd),
          filter(e => !e.snapshot.firstChild) // hoja final SIEMPRE
        )
        .subscribe(e => {

          // 1) ThemeColor (lo que ya tenías)
          const color = e.snapshot.data?.['themeColor'] ?? DEFAULT_THEME;
          this.routeThemeColor.set(color);

          // 2) ThemeClass (NUEVO)
          const themeClass = e.snapshot.data?.['themeClass'];

          // 👉 Siempre limpiamos las clases (solo las nuestras)
          /* Este codigo es remplazado por el de abajo
          document.body.classList.forEach(cls => {
            if (cls.startsWith('bg-')) {
              document.body.classList.remove(cls);
            }
          });*/

          // ⚠️ Mejor evitar mutar classList mientras iterás
          // limpia TODAS las clases 'theme-*'
          Array.from(document.body.classList).forEach(cls => {
            if (cls.startsWith('bg-')) document.body.classList.remove(cls);
          });

          // 👉 Solo aplicamos una clase si la ruta la define
          if (themeClass) {
            document.body.classList.add(themeClass);
          }
        });

      this.subs.add(themeSub);

      // ==================================================
      // 🔵 DETECTAR NUEVA VERSIÓN DEL PWA
      // ==================================================
      if (this.swUpdate?.isEnabled) {
        const sub = this.swUpdate.versionUpdates
          .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
          .subscribe((evt) => {
            console.info(`current=[${evt.currentVersion}]; latest=[${evt.latestVersion}]`);
            this.modalVersion.set(true); // muestra el modal
          });
        this.subs.add(sub);
      }

      // ==================================================
      // 🔵 OCULTAR NAVBAR SEGÚN LA RUTA 
      // ==================================================
      const navSub = this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => {
          const url = this.router.url;
          this.hideNavbar.set(this.hideNavbarRoutes.some(r => url.startsWith(r)));
          this.fullBleedLeft.set(this.fullBleedLeftRoutes.some(r => url.startsWith(r)));
          // screen_view en nativo (en web lo cubre ScreenTrackingService)
          void this._analytics.setScreen(url.split('?')[0] || '/');
        });
      this.subs.add(navSub);
      } catch (e) {
      console.error('Error en App ngOnInit', e);
    }
    void this._updateCheck.init();
  }

  private async setupDeepLinks(): Promise<void> {
    try {
      // Caso 1: la app se abre desde cero tocando un link
      const launch = await App.getLaunchUrl();
      if (launch?.url) {
        this.handleDeepLink(launch.url);
      }

      // Caso 2: la app ya está abierta o en background
      await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        if (!event?.url) return;
        this.handleDeepLink(event.url);
      });
    } catch (error) {
      console.error('Error configurando deep links:', error);
    }
  }

  private handleDeepLink(rawUrl: string): void {
    try {
      console.log('Deep link recibido:', rawUrl);

      const url = new URL(rawUrl);

      // Seguridad: solo procesamos links de tu dominio
      if (url.host !== 'app.libresalud.com.ar') return;

      const path = url.pathname + url.search + url.hash;

      void this.router.navigateByUrl(path);
    } catch (error) {
      console.error('Error procesando deep link:', error, rawUrl);
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (IS_NATIVE) {
      // Pequeño micro-turno: evita un frame negro entre LaunchScreen y WebView
      await new Promise(r => setTimeout(r, 0));
      try { await SplashScreen.hide(); } catch {}
    }
  }

  async updateNow() {
    await this._updateCheck.openStore();
  }

  closeUpdate() {
    this._updateCheck.close();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public async updateVersion(): Promise<void> {
    this.modalVersion.set(false);
    await this._authService.logout();
    window.location.reload();
  }

  public closeVersion(): void {
    this.modalVersion.set(false);
  }

  async reintentarNetwork() {
    await this._network.refresh('button');
  }
}